import { Injector } from "@angular/core";
import { loadModules } from "esri-loader";
import { Observable, of, from, empty } from "rxjs";
import { concatMap } from "rxjs/operators";
import { Renderer, MAP_ELEMENT, CONFIG } from "./renderer.interface";
import esri = __esri;

export class Renderer3D extends Renderer<esri.SceneView> {
    protected rendererConfig: any;
    defaultCameraPosition: { point: { x?: number, y?: number, z?: number }, tilt?: number, heading?: number };
    scene: esri.WebScene;
    camera: esri.Camera;

    set properties(val: any) {
        if (!this.rendererConfig)
            this.rendererConfig = {};

        this.rendererConfig.properties = val;
    }

    public constructor(protected injector: Injector) {
        super(injector);
        this.rendererConfig = Object.assign(this.defaultConfig(), this.injector.get(CONFIG, this.defaultConfig()));
        this.containerElement = injector.get(MAP_ELEMENT);
    }

    export(): Observable<any> {
        return from(this.view.takeScreenshot());
    }

    render(): Observable<any> {
        return empty();
    }

    initialize(): Observable<esri.SceneView> {
        this.rendererConfig = Object.assign(this.defaultConfig(), this.injector.get(CONFIG, this.defaultConfig()));
        return from(loadModules([
            "esri/views/SceneView",
            "esri/WebScene",
            "esri/geometry/Extent",
            "esri/Camera",
            "esri/geometry/Point"
        ])).pipe(concatMap(([SceneView, WebScene, Extent, Camera, Point]: [
            esri.SceneViewConstructor, esri.WebSceneConstructor, any, any, any, any]) => {
            this.scene = new WebScene({
                basemap: 'satellite',
                ground: 'world-elevation',
            });

            this.rendererConfig.properties.map = this.scene;
            this.rendererConfig.properties.center = [this.rendererConfig.center.longitude || 0, this.rendererConfig.center.latitude || 0]
            this._view = new SceneView(this.rendererConfig.properties);
            this.camera = new Camera({
                position: new Point({
                    x: this.rendererConfig.center.longitude,
                    y: this.rendererConfig.center.latitude,
                }),
                heading: 0,
                tilt: 30
            });

            this.defaultCameraPosition = {
                point: { x: this.camera.position.x, y: this.camera.position.y, z: this.camera.position.z },
                heading: this.camera.heading,
                tilt: this.camera.tilt
            }

            this._view.when(() => {
                this.scene.ground.navigationConstraint = {
                    type: "none"
                };

                this._view.goTo(this.camera);

            });

            return of(this._view);
        }));
    }

    async setCameraPosition(point: { x?: number, y?: number, z?: number }, heading?: number, tilt?: number) {
        const [Point] = await loadModules([
            "esri/geometry/Point"
        ]);

        this.camera.position = new Point({
            x: point.x || this.camera.position.x,
            y: point.y || this.camera.position.y,
            z: point.z || this.camera.position.z
        });



        if (heading)
            this.camera.heading = heading;
        if (tilt)
            this.camera.tilt = tilt;

        return this._view.goTo(this.camera);
    }

    setRot(val: number) {
        this.camera.heading = val;
        this._view.goTo(this.camera);
    }

    defaultConfig() {
        return {
            properties: {
                constraints: {
                    collision: {
                        enabled: false
                    },
                    tilt: {
                        max: 179.99
                    }
                },
                highlightOptions: {
                    color: [255, 127, 127],
                    haloOpacity: 0.3,
                    fillOpacity: 0.6
                },
                center: [
                    0,
                    0
                ],
                viewingMode: "local",
                container: this.containerElement,
                map: this.scene
            } as esri.SceneViewProperties
        };
    }

    resetCameraPosition() {
        this.setCameraPosition(this.defaultCameraPosition.point, this.defaultCameraPosition.heading, this.defaultCameraPosition.tilt);
    }
}

export interface SceneViewConfig {
    properties: any;
}
